import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Toaster, toast } from 'sonner';
import {
  Building2, Users, Scale, Wheat, Banknote, Heart, BarChart3,
  LogOut, Download, Filter, ChevronRight, MapPin, Eye
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const cfg = { withCredentials: true };
const fmtRp = (n) => new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(n || 0);
const fmtNum = (n) => Number(n || 0).toLocaleString('id-ID');

const PERIODS = [
  { id: 'harian', label: 'Hari Ini' },
  { id: 'mingguan', label: 'Minggu Ini' },
  { id: 'bulanan', label: 'Bulan Ini' },
  { id: 'ramadan', label: 'Ramadan' },
];

const StatCard = ({ icon: Icon, color, label, value, unit }) => (
  <div className="bg-white rounded-2xl border border-stone-200 p-5">
    <div className={`w-10 h-10 rounded-xl ${color} flex items-center justify-center mb-3`}>
      <Icon className="w-5 h-5 text-white" />
    </div>
    <p className="text-xs text-stone-500 mb-1">{label}</p>
    <p className="text-xl font-bold text-stone-800">{value}</p>
    {unit && <p className="text-xs text-stone-400 mt-0.5">{unit}</p>}
  </div>
);

export default function KemenagDashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [rekap, setRekap] = useState([]);
  const [period, setPeriod] = useState('bulanan');
  const [selectedMosque, setSelectedMosque] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  const role = user?.role;
  const roleLabel = role === 'kemenag_prov' ? 'Kemenag Provinsi' : role === 'kemenag_kab' ? 'Kemenag Kab/Kota' : 'Super Admin';
  const regionName = user?.region_name || '';

  const loadStats = async (p = period) => {
    setLoading(true);
    try {
      const [statsRes, rekapRes] = await Promise.all([
        axios.get(`${API_URL}/api/zakat-ramadan/kemenag/stats`, { ...cfg, params: { period: p } }),
        axios.get(`${API_URL}/api/zakat-ramadan/kemenag/rekap`, { ...cfg, params: { period: p, limit: 50 } }),
      ]);
      setStats(statsRes.data);
      setRekap(rekapRes.data.data);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error('Anda tidak memiliki akses ke halaman ini');
        navigate('/login');
      }
    } finally { setLoading(false); }
  };

  const loadDetail = async (mosqueId) => {
    try {
      const { data } = await axios.get(`${API_URL}/api/zakat-ramadan/kemenag/detail/${mosqueId}`, { ...cfg, params: { period } });
      setDetailData(data);
    } catch {}
  };

  useEffect(() => {
    if (!user) return;
    if (!['kemenag_kab', 'kemenag_prov', 'super_admin'].includes(role)) {
      navigate('/login');
      return;
    }
    loadStats();
  }, [user]);

  const handleDownloadExcel = async () => {
    setDownloading(true);
    try {
      const response = await axios.get(`${API_URL}/api/zakat-ramadan/kemenag/export-excel`, {
        ...cfg, params: { period }, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `rekap-zakat-kemenag-${period}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Gagal mengunduh'); }
    finally { setDownloading(false); }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-stone-50">
      <Toaster position="top-right" richColors />

      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-800 to-emerald-900 text-white sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-heading font-bold">Dashboard Zakat Ramadan</h1>
            <p className="text-emerald-200 text-sm">{roleLabel} — {regionName}</p>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-emerald-200">{user?.name}</span>
            <button onClick={handleLogout} className="p-2 rounded-full hover:bg-white/10 transition-colors">
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8 space-y-6">

        {/* Period filter */}
        <div className="flex items-center gap-3 flex-wrap">
          {PERIODS.map(p => (
            <button key={p.id} onClick={() => { setPeriod(p.id); loadStats(p.id); }}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${period === p.id ? 'bg-emerald-700 text-white' : 'bg-white border border-stone-200 text-stone-600 hover:bg-stone-50'}`}>
              {p.label}
            </button>
          ))}
          <button onClick={handleDownloadExcel} disabled={downloading}
            className="ml-auto flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-full text-sm font-medium transition-colors disabled:opacity-60">
            <Download className="w-4 h-4" /> {downloading ? 'Mengunduh...' : 'Export Excel'}
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-10 h-10 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
              <StatCard icon={Building2} color="bg-emerald-700" label="Total Masjid/UPZ" value={fmtNum(stats?.total_masjid)} />
              <StatCard icon={Users} color="bg-blue-600" label="Total Muzakki" value={fmtNum(stats?.total_muzakki)} unit="KK" />
              <StatCard icon={Scale} color="bg-teal-600" label="Total Jiwa" value={fmtNum(stats?.total_jiwa)} unit="jiwa" />
              <StatCard icon={Wheat} color="bg-amber-600" label="Zakat Beras" value={`${fmtNum(stats?.total_beras_kg)} kg`} />
              <StatCard icon={Banknote} color="bg-green-600" label="Zakat Uang" value={fmtRp(stats?.total_uang_rp)} />
              <StatCard icon={Heart} color="bg-rose-500" label="Total Infak" value={fmtRp(stats?.total_infak_rp)} />
              <StatCard icon={BarChart3} color="bg-stone-700" label="Total Transaksi" value={fmtNum(stats?.total_transaksi)} />
            </div>

            {/* Detail modal */}
            {selectedMosque && detailData && (
              <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                <div className="bg-white rounded-2xl max-w-2xl w-full shadow-xl max-h-[80vh] overflow-y-auto">
                  <div className="sticky top-0 bg-white px-5 py-4 border-b flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-stone-800">{detailData.mosque?.name}</h3>
                      <p className="text-xs text-stone-500">{detailData.profile?.nama_amil || 'Amil'}</p>
                    </div>
                    <button onClick={() => { setSelectedMosque(null); setDetailData(null); }}
                      className="p-2 rounded-lg hover:bg-stone-100 text-stone-500">✕</button>
                  </div>
                  <div className="p-5 space-y-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {[
                        ['Total Jiwa', `${fmtNum(detailData.stats?.total_jiwa)} jiwa`],
                        ['Zakat Beras', `${fmtNum(detailData.stats?.total_beras_kg)} kg`],
                        ['Zakat Uang', fmtRp(detailData.stats?.total_uang_rp)],
                        ['Total Infak', fmtRp(detailData.stats?.total_infak_rp)],
                      ].map(([label, val]) => (
                        <div key={label} className="bg-stone-50 rounded-xl p-3 text-center">
                          <p className="text-xs text-stone-500">{label}</p>
                          <p className="font-bold text-stone-800 text-sm">{val}</p>
                        </div>
                      ))}
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead className="bg-stone-50 text-stone-500">
                          <tr>
                            <th className="px-3 py-2 text-left">No. Transaksi</th>
                            <th className="px-3 py-2 text-left">Tanggal</th>
                            <th className="px-3 py-2 text-left">Muzakki</th>
                            <th className="px-3 py-2 text-center">Jiwa</th>
                            <th className="px-3 py-2 text-right">Zakat</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-stone-100">
                          {detailData.transactions?.map(row => (
                            <tr key={row.id} className="hover:bg-stone-50">
                              <td className="px-3 py-2 font-mono text-stone-400">{row.nomor_transaksi}</td>
                              <td className="px-3 py-2 text-stone-600">{row.tanggal}</td>
                              <td className="px-3 py-2 font-medium text-stone-800">{row.nama_kk}</td>
                              <td className="px-3 py-2 text-center text-emerald-700">{row.jumlah_jiwa}</td>
                              <td className="px-3 py-2 text-right font-semibold">
                                {row.jenis_pembayaran === 'beras' ? `${row.total_zakat_beras}kg` : fmtRp(row.total_zakat_uang)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Rekap per masjid */}
            <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
              <div className="px-5 py-4 border-b border-stone-100 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-emerald-600" />
                  <span className="font-semibold text-stone-700 text-sm">Rekap per Masjid/UPZ</span>
                </div>
              </div>
              {rekap.length === 0 ? (
                <div className="p-10 text-center text-stone-400 text-sm">Belum ada data transaksi pada periode ini</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-stone-50 text-xs text-stone-500">
                      <tr>
                        <th className="px-4 py-3 text-left">Masjid/UPZ</th>
                        <th className="px-4 py-3 text-left">Kecamatan</th>
                        <th className="px-4 py-3 text-left">Amil</th>
                        <th className="px-4 py-3 text-center">Jiwa</th>
                        <th className="px-4 py-3 text-right">Beras (kg)</th>
                        <th className="px-4 py-3 text-right">Uang (Rp)</th>
                        <th className="px-4 py-3 text-right">Infak (Rp)</th>
                        <th className="px-4 py-3 text-center">Transaksi</th>
                        <th className="px-4 py-3 text-center">Detail</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-stone-100">
                      {rekap.map(row => (
                        <tr key={row.mosque_id} className="hover:bg-stone-50 transition-colors">
                          <td className="px-4 py-3 font-medium text-stone-800">{row.mosque_name}</td>
                          <td className="px-4 py-3 text-stone-500">{row.district_name || '-'}</td>
                          <td className="px-4 py-3 text-stone-600">{row.nama_amil || '-'}</td>
                          <td className="px-4 py-3 text-center font-semibold text-emerald-700">{fmtNum(row.total_jiwa)}</td>
                          <td className="px-4 py-3 text-right text-amber-700 font-medium">{fmtNum(row.total_beras_kg)}</td>
                          <td className="px-4 py-3 text-right text-blue-700 font-medium">{fmtRp(row.total_uang_rp)}</td>
                          <td className="px-4 py-3 text-right text-rose-600">{fmtRp(row.total_infak_rp)}</td>
                          <td className="px-4 py-3 text-center">
                            <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium">{row.total_transaksi}</span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <button
                              onClick={() => { setSelectedMosque(row.mosque_id); loadDetail(row.mosque_id); }}
                              className="p-1.5 rounded-lg hover:bg-emerald-50 text-emerald-600 transition-colors">
                              <Eye className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
